import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { SIGN_UP_URL, useAuth } from '../api.ts';

import { Layout } from './Layout.tsx';

const SLOW_LOAD_MS = 4_000;

/**
 * Route guard for the authenticated app. A signed-out visitor — whether
 * they hit `/` or deep-linked to `/accounting`, `/settings`, etc. — is
 * sent straight to the WorkOS hosted sign-up screen. There is no in-app
 * marketing or custom sign-up surface to land on; the WorkOS UI is the
 * sign-up screen.
 *
 * Branches:
 *   - loading: minimal shell (deep links don't flash the redirect)
 *   - error:   surface message + reload (don't bounce to sign-up on a
 *              transient API failure)
 *   - signed-out: full-page redirect to the WorkOS sign-up page
 *   - signed-in:  render the matched child route
 */
export function RequireAuth() {
  const auth = useAuth();
  const slow = useSlowLoadFlag(auth.status === 'loading');

  useEffect(() => {
    if (auth.status === 'signed-out') {
      // Full-page navigation: SIGN_UP_URL is the API origin, which 302s
      // to the WorkOS hosted sign-up page. `replace` so the back button
      // doesn't bounce the user back into the redirect.
      window.location.replace(SIGN_UP_URL);
    }
  }, [auth.status]);

  if (auth.status === 'signed-in') {
    return <Outlet />;
  }

  return (
    <Layout title="Technical Accounting">
      {auth.status === 'error' ? (
        <ErrorBody message={auth.message} />
      ) : auth.status === 'signed-out' ? (
        <p className="muted">Taking you to sign-up…</p>
      ) : (
        <LoadingBody slow={slow} />
      )}
    </Layout>
  );
}

function LoadingBody({ slow }: { slow: boolean }) {
  return (
    <>
      <p className="muted">Loading…</p>
      {slow ? (
        <p className="muted">
          Taking longer than expected. If it doesn&apos;t resolve in a few seconds, try{' '}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.location.reload();
            }}
          >
            reloading
          </a>
          .
        </p>
      ) : null}
    </>
  );
}

function ErrorBody({ message }: { message: string }) {
  return (
    <>
      <h1 className="view-h1">Couldn&apos;t reach the API</h1>
      <p className="view-subtitle">{message}</p>
      <p>
        <a
          href="#"
          className="btn"
          onClick={(e) => {
            e.preventDefault();
            window.location.reload();
          }}
        >
          Try again
        </a>
      </p>
    </>
  );
}

function useSlowLoadFlag(loading: boolean): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!loading) {
      setSlow(false);
      return;
    }
    const t = setTimeout(() => setSlow(true), SLOW_LOAD_MS);
    return () => clearTimeout(t);
  }, [loading]);
  return slow;
}
